import { useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useFinance } from '@/hooks/useFinance';
import { expenseCategoryMeta, formatMoney } from '@/lib/finance';
import { FinanceSection } from '../components/FinanceUI';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
}

const SUGGESTIONS = [
  'Combien ai-je gagné ce mois-ci ?',
  'Combien ai-je dépensé ?',
  'Combien mes clients me doivent-ils ?',
  'Combien dois-je à mes fournisseurs ?',
  'Quel est mon bénéfice estimé ?',
  'Résume ma situation financière.',
  'Compare ce mois avec le mois dernier.',
  'Quelle est ma trésorerie prévue pour les 30 prochains jours ?',
];

export default function FinanceAiPage() {
  const { user } = useAuth();
  const finance = useFinance();
  const { currency, accounts, accountBalances, transactions, expenses, invoices, suppliers } = finance;

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const summary = useMemo(() => {
    const revenueTotal = transactions.filter((t) => t.direction === 'in').reduce((s, t) => s + Number(t.amount), 0);
    const expenseTotal = expenses.reduce((s, e) => s + Number(e.amount), 0);
    const byCat = new Map<string, number>();
    expenses.forEach((e) => byCat.set(e.category, (byCat.get(e.category) || 0) + Number(e.amount)));
    const topCats = [...byCat.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([c, v]) => `${expenseCategoryMeta(c).label}: ${formatMoney(v, currency)}`).join(', ');
    const pending = invoices.filter((i) => ['sent', 'partially_paid', 'overdue'].includes(i.status))
      .reduce((s, i) => s + Math.max(0, i.total - i.amount_paid), 0);

    return [
      `DONNÉES FINANCIÈRES RÉELLES (module Zifek Finance)`,
      `Devise : ${currency}`,
      `Revenus enregistrés : ${formatMoney(revenueTotal, currency)}`,
      `Dépenses enregistrées : ${formatMoney(expenseTotal, currency)}`,
      `Bénéfice estimé : ${formatMoney(revenueTotal - expenseTotal, currency)}`,
      `Trésorerie disponible : ${formatMoney(finance.totalCash, currency)}`,
      `Comptes : ${accounts.map((a) => `${a.name} = ${formatMoney(accountBalances[a.id] || 0, currency)}`).join(' ; ') || 'aucun'}`,
      `Créances clients : ${formatMoney(finance.receivables, currency)}`,
      `Dettes fournisseurs : ${formatMoney(finance.payables, currency)}`,
      `Factures en attente (non soldées) : ${formatMoney(pending, currency)}`,
      `Nombre de fournisseurs : ${suppliers.length}`,
      topCats ? `Principales dépenses : ${topCats}` : '',
    ].filter(Boolean).join('\n');
  }, [currency, transactions, expenses, invoices, suppliers, accounts, accountBalances, finance.totalCash, finance.receivables, finance.payables]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || loading || !user) return;
    setInput('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('mon-assistant', {
        body: {
          message: `Contexte financier disponible (utilise uniquement ces données réelles) :\n${summary}\n\nQuestion du commerçant : ${q}\n\nRappel : distingue clairement les chiffres réels des estimations, et ne propose de créer une écriture que si je le confirme explicitement.`,
          userId: user.id,
        },
      });
      if (fnErr) throw fnErr;
      const payload = data as { reply?: string; error?: string };
      if (payload?.error) throw new Error(payload.error);
      setMessages((prev) => [...prev, { role: 'assistant', content: payload?.reply || 'Pas de réponse.' }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de l’assistant');
    } finally {
      setLoading(false);
      window.setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 60);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-primary-50 border border-primary-200/60 rounded-lg p-4 flex items-start gap-3">
        <i className="ri-sparkling-line text-primary-600 text-xl mt-0.5"></i>
        <div className="text-sm text-primary-900">
          <strong>Ask Zifek Finance AI</strong> — posez vos questions, l&apos;assistant analyse <strong>vos données financières réelles</strong> et
          celles de votre boutique. Il ne crée jamais d&apos;écriture sans votre confirmation, et distingue toujours les chiffres réels des estimations.
        </div>
      </div>

      <FinanceSection title="Questions rapides" icon="ri-lightbulb-line">
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)} disabled={loading} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
              {s}
            </button>
          ))}
        </div>
      </FinanceSection>

      <FinanceSection title="Conversation" icon="ri-chat-3-line">
        <div ref={scrollRef} className="max-h-[46vh] overflow-y-auto space-y-3 mb-4 pr-1">
          {messages.length === 0 ? (
            <p className="text-sm text-foreground-400 text-center py-8">
              Posez une question ou cliquez sur une suggestion pour commencer.
            </p>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-primary-500 text-background-50 rounded-br-md' : 'bg-background-100 text-foreground-800 rounded-bl-md'
                }`}>
                  {m.content}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-background-100 text-foreground-500 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>Analyse de vos données...
              </div>
            </div>
          )}
        </div>

        {error && <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs flex items-start gap-2"><i className="ri-error-warning-line mt-0.5"></i>{error}</div>}

        <form
          onSubmit={(e) => { e.preventDefault(); ask(input); }}
          className="flex items-center gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ex : Combien ai-je gagné ce mois-ci ?"
            className="flex-1 px-4 py-2.5 rounded-full text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
          <button type="submit" disabled={loading || !input.trim()} className="w-11 h-11 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-send-plane-fill"></i>
          </button>
        </form>
      </FinanceSection>
    </div>
  );
}